export const en = {
	setupWizard: {
		// Steps
		STEP_1_TITLE: 'Get started',
		STEP_1_SUBTITLE: 'Set up the basics of your design',
		STEP_2_TITLE: 'Choose your company logo',
		STEP_2_SUBTITLE: 'Choose a logo to appear in the page header and/or footer',
		STEP_3_TITLE: 'Turn products into virtual samples',
		STEP_3_SUBTITLE:
			'Chose a logo to be applied to the products you add to your design',

		// Form fields
		ADD_NAME: 'Give your design a name',
		SELECT_CURRENCY: 'Choose a currency',
		USE_AUTO_LOGO: 'Automatically add logo to your design',
		SELECT_LOGO: 'Select logo',
		UNTITLED: 'Untitled',
		SELECT_YOUR_DESIGN_TYPE: 'Orientation',

		// Orientation
		PORTRAIT: 'Portrait',
		LANDSCAPE: 'Landscape',

		// Buttons
		BACK: 'Back',
		CONTINUE: 'Continue',
		SKIP: 'Skip',
		STEP_OF: 'Step {current} of {total}',

		// Messages
		NAME_IS_REQUIRED:
			'The field name is required and must be less than 64 characters',
		UNABLE_TO_SAVE_DESIGN_SETTINGS:
			'Unable to save design settings, please try again',
	},

	pagination: {
		ADD_PAGE: 'Add a page',
		SINGLE_PAGE: 'Single page',
		SPREAD_PAGE: 'Spread page',
		DELETE_PAGE: (data: Record<string, any>) =>
			(data.values.count ?? 0) > 1
				? `Delete ${data.values.count} pages`
				: 'Delete page',
		DUPLICATE_PAGE: 'Duplicate page',
		DELETE_CONFIRMATION: (data: Record<string, any>) =>
			(data.values.count ?? 0) > 1
				? `Are you sure you want to delete the ${data.values.count} selected pages?`
				: 'Are you sure you want to delete this page?',
		YOU_CANNOT_SELECT_MORE_THAN: (data: Record<string, any>) =>
			`You cannot select more than ${data.values.count} pages at once`,
	},

	startScreen: {
		// Header
		TITLE: 'Get Started',
		SUBTITLE: 'Create new, start from a template, or open an existing project',
		NEW_DESIGN: 'Create a new design',

		// Sections
		MY_DESIGNS: 'My Designs',
		TEMPLATES: 'Templates',
		TEMPLATES_SS: 'S&S Templates',
		BROWSE_DESIGNS: 'Browse my designs',
		CONTINUE_WORKING: 'Continue working on your designs',

		// Design actions
		CREATE: 'Create',
		EDIT: 'Edit',
		USE_TEMPLATE: 'Use template',

		// Design card
		CREATE_NEW_DESIGN: 'Create a new design',

		// Options menu
		VIEW_DESIGN: 'View design',
		DUPLICATE_DESIGN: 'Duplicate design',
		DELETE_DESIGN: 'Delete design',

		// Delete confirmation
		DESIGN: 'Design',
		DELETE_CONFIRMATION: 'Are you sure you want to delete the design',
		DELETE_YES: 'Yes, delete design',
		CANCEL: 'Cancel',
		DELETE_SUCCESS: 'The design was successfully deleted',

		// Loading
		LOADING_MORE: 'Loading more',
		NO_MORE_RESULTS: 'No more results',
	},

	setupWizardCompleted: {
		TITLE: 'All set! Start your design now.',
		DESCRIPTION: 'You can edit these settings any time by clicking on',
		DESIGN_SETTINGS: 'Design Settings',
		CONTINUE_TO_DESIGN: 'Continue to your design',
		CONTINUE_TO_ADD_PRODUCTS: 'Continue to add products',
		PROCESSING: 'Processing...',
	},

	toolbar: {
		// Publish
		SHARE: 'Share',
		PUBLISH_DESIGN: 'Publish design',
		PUBLISH_CHANGES: 'Publish changes',
		DESIGN_PRIVATE: 'This design is private.',
		SHARE_YOUR_DESIGN_INSTANTLY: 'Share your Design, Instantly!',
		SHARE_YOUR_DESIGN_INSTANTLY_DESCRIPTION:
			'Publish your design to get a shareable link for emails, websites, social media and more!',

		// Design Settings
		DESIGN_SETTINGS: 'Design settings',
		DRAFT_SAVED: 'Draft saved',
		SAVING: 'Saving...',
		MANAGE_YOUR_DESIGN_SETTINGS: 'Manage your Design Settings',
		UPDATE_YOUR_DESIGN_NAME_AND_LOGO:
			'Update your design name and logo, manage your Virtual Sample settings, and more.',

		// Manage Page
		MANAGE_PAGE: 'Manage page',
		LEFT_PAGE: 'Left page',
		RIGHT_PAGE: 'Right page',
		REORDER_PAGES: 'Reorder pages',
		IS_TEMPLATE: 'This design is a template',
		TEMPLATE_TEXT:
			'Share this design as template to let others duplicate and customize it as their own.',
		WATCH_TUTORIAL: 'Watch tutorial',
		CHANGE_THE_PAGE_LAYOUT: 'Change the Page Layout',
		CHANGE_THE_PAGE_LAYOUT_DESCRIPTION:
			'Cover page? Two products and a banner? Single featured product? Choose the perfect page layout here!',

		// Errors
		INVALID_PROJECT:
			'Missing project details. Please try again or contact support.',
	},

	pageSidebar: {
		MANAGE_PAGE: 'Manage Page',
		SELECT_LAYOUT_TITLE: 'Select page layout',
		PAGE_SETTINGS_TITLE: 'Product information on page',
		MANAGE_PAGE_CONTENT: 'Manage page content',
		SIDEBAR_SETTINGS_TITLE: 'Product information in sidebar',
		CHANGE_LAYOUT_BTN: 'Change layout',
		LAYOUT_NAMES: {
			BASIC_FOUR: 'Basic 4 Products',
		},
	},

	designSettings: {
		DESIGN_SETTINGS: 'Design settings',
		GENERAL: 'General',
		TEMPLATE: 'Template',
		VIRTUALS: 'Virtual samples',
		DATA: 'Data',
		CREATE_DESIGN: 'Create design',
		SAVE_SETTINGS: 'Save settings',
		NAME_IS_REQUIRED: 'Name is required',
		UNABLE_TO_SAVE_DESIGN_SETTINGS: 'Unable to save design settings',
		MARK_UP_DOWN_PRICES: 'Mark up / down prices',
		DISCOUNT: 'Discount',
		MARK_UP: 'Mark up',
		OVERRIDE_SELECTED_DATA: 'Override selected data',
		PAGE_VISIBILITY: 'Page visibility',
		SIDEBAR_VISIBILITY: 'Sidebar visibility',
		SELECT_DATA_TO_INCLUDE: 'Select the data you want to include',
		PAGE: 'Page',
		SIDEBAR: 'Sidebar',
		ADD_NAME: 'Add a name to your design',
		SELECT_CURRENCY: 'Select the currency for this design',
		USE_AUTO_LOGO: 'Automatically add logo to your design',
		SELECT_LOGO: 'Select your logo',
		UNTITLED: 'Untitled',
		SELECT_YOUR_DESIGN_TYPE: 'Select your design type',
		PORTRAIT: 'Portrait',
		LANDSCAPE: 'Landscape',
	},

	userMenu: {
		NEW: 'Create New Design',
		MY_DESIGNS: 'My Designs',
		DASHBOARD: 'Dashboard',
		SIGN_OUT: 'Sign Out',
	},

	layoutPicker: {
		SELECT_LAYOUT: 'Select a page layout',
		LAYOUT_CATEGORIES: 'Layout categories',
		ALL_LAYOUTS: 'All layouts',
		COVER_PAGES: 'Cover pages',
		BASIC_LAYOUTS: 'Basic layouts',
		GRAPHIC_LAYOUTS: 'Graphic layouts',
		VIRTUALS_LAYOUTS: 'Virtuals layouts',
		TEXT_LAYOUTS: 'Text layouts',

		// Help section
		NEED_NEW_TEMPLATE: 'Need a new template?',
		TEMPLATE_LIBRARY_GROWING:
			"The template library is growing fast! Tell us what you're looking for.",
		SEND_SUGGESTION: 'Send a suggestion',

		// Empty state
		MORE_TEMPLATES_COMING: 'More templates are coming soon',
		ALWAYS_ADDING_NEW: "We're always adding new templates. Check back soon.",

		// Layout actions
		USE_LAYOUT: 'Use layout',
		SELECTED: 'Selected',
		NOT_AVAILABLE: 'Not available',
		COMING_SOON: 'Coming soon',
		LAYOUT_NOT_AVAILABLE_TEXT:
			'You have added more products to the canvas than this layout supports. To use it, remove some products.',
	},

	product: {
		ALL_COLORS: 'All Colors',
	},

	publishModal: {
		PUBLISH_TEMPLATE: 'Publish template',
		PUBLISH_TEMPLATE_CHANGES: 'Publish template changes',
		PUBLISH_TEMPLATE_TITLE: 'Are you ready to publish your template?',
		PUBLISH_TEMPLATE_TEXT:
			"Once you've published your design as a template, you can share it with other people so they can easily customize it as their own.",
		PUBLISH_CHANGES: 'Publish changes',
		PUBLISH_CHANGES_TITLE:
			'Are you sure you want to publish these changes to your template?',
		PUBLISH_CHANGES_TEXT:
			'These changes will be made public to those who have access to your design in the viewer. This can not be undone.',
		PUBLISH_DESIGN: 'Are you ready to publish your design and begin sharing?',
		YES_PUBLISH: 'Yes, publish',
		CANCEL: 'Cancel',
	},

	shareModal: {
		SHARE_DESIGN: 'Share your design',
		SHARE_TEMPLATE: 'Share your template',
		SEND_LINK_TITLE:
			'Share your design via email, social media, your website, and more.',
		SEND_LINK_TITLE_TEMPLATE: 'Are you ready to share your template?',
		SEND_LINK_DESCRIPTION: 'Just copy and paste the link below.',
		COPY_LINK: 'Copy link',
		COPY_LINK_TEMPLATE: 'Copy link to your template',
		COPY_LINK_DESCRIPTION: 'Anyone with this link can view your design.',
		COPY_LINK_DESCRIPTION_TEMPLATE:
			'This means anyone with your sharable link can view your design.',
		MAKE_PRIVATE: 'Make it private?',
		MAKE_PRIVATE_DESCRIPTION:
			"If you make your design private, people with the link won't be able to see it.",
		OPEN_LINK: 'Open link',
	},

	downloadBar: {
		DOWNLOAD: 'Download PDF',
	},

	productDataControl: {
		MANAGE_PRODUCT: 'Manage product',
		PRODUCT_DATA: 'Product data',
		PAGE: 'Page',
		CANVAS: 'Page',
		SIDEBAR: 'Sidebar',
		DATA: 'Data',
		TEMPLATE: 'Template',
		UPDATE_ALL: 'Update All',
		COLORS: 'Colors',
		COLOR: 'Color',
		SIZES: 'Sizes',
		NO_IMAGES: 'No images',
		NO_IMAGES_SELECTED: 'No images selected',
		SEE_ALL: 'See all',
		NO_COLORS_SELECTED: 'No colors selected',
		NO_SIZES_SELECTED: 'No sizes selected',
		UPLOADS: 'Uploads',
		DEFAULT: 'Default',
		BASIC: 'Basic',
		QUANTITY: 'Quantity',
		PRICE: 'Price',
		ACTIONS: 'Actions',
		PRICING_CODE: 'Pricing code',
		PRICING_TABLE: 'Pricing table',
		SINGLE_PRICE: 'Single price',
		MOQ_IS_AUTOMATICALLY_POPULATED:
			'MOQ is automatically populated based on your pricing data.',
		ALLOW_REPLACING_THIS_PRODUCT: 'Allow replacing this product?',
		ALLOW_EDITING_PRODUCT_INFORMATION: 'Allow editing product information?',
		YES_THIS_PRODUCT_CAN_BE_REPLACED: 'Yes, this product can be replaced.',
		YES_THE_PRODUCT_INFORMATION_CAN_BE_EDITED:
			'Yes, the product information can be edited.',
		EDITING_DISABLED_FOR_THIS_PRODUCT:
			'Editing has been disabled for this product by the template creator.',
		PLEASE_PROVIDE_A_VALID_PRICE: 'Please provide a valid price',
		OTHER_FIELDS: 'Other fields',
		HERE_YOU_CAN_EDIT_THE_PRODUCT_DATA:
			'Here you can edit the product data for this field',
		NO_MORE_OPTIONS_TO_SELECT: 'No more options to select',
		VIRTUALS: 'Virtuals',
		YOUR_LOGO: 'Your logo',
		PICK_IMPRINT_AREA: 'Pick imprint area',
		CREATE: 'Create',
		YOUR_VIRTUALS: 'Your virtuals',
		NO_VIRTUALS_ENABLED: 'No virtuals enabled',
		NO_VIRTUALS_ANYMORE: 'No virtuals anymore',
		NO_VIRTUALS_CREATED: 'No virtuals created',
		NO_IMPRINT_AREAS: 'No imprint areas',
		SELECTED_IMAGE: 'Selected image',
		UNSELECTED_IMAGE: 'Unselected image',
		VS: 'VS',
		ADD_LOGO: 'Add logo',
		ALL_IMAGES: 'All images',
		MY_UPLOADS: 'My uploads',
		MY_VIRTUALS: 'My virtuals',
		VIRTUAL_SAMPLE_ENABLED: 'Virtual sample enabled',
		PRODUCT_IMAGES: 'Product images',
		ALL_COLORS: 'All colors',
		CREATE_VIRTUAL_SAMPLES: 'Create Virtual Samples',
		UNNAMED: 'Unnamed',
		CALCULATED_PRICING: 'Calculated pricing',
		DISCOUNT: 'Discount',
		MARK_UP: 'Mark up',
		ENABLED: 'Enabled',
		DISABLED: 'Disabled',
		ADD_ALL: 'Add all',
		REMOVE_ALL: 'Remove all',
		USE_LIST_PRICING: 'Use list pricing',
		ADD_MY_OWN_PRICING: 'Add my own pricing',
		NO_PRICING: 'No pricing',
		YOU_HAVE_SELECTED_TO_REMOVE_ANY_PRICING:
			'You have selected to remove any pricing from this product.',
		DO_YOU_WANT_TO_ADD_PRICE_PLACEHOLDER:
			'Do you want to add a price placeholder?',
		NO_LEAVE_IT_BLANK: 'No, leave it blank',
		YES_ADD_CALL_FOR_PRICING: 'Yes, add "Call for pricing"',
		PRICE_CODE: 'Price code',
		INCLUDE_PRICE_CODE: 'Include price code',
		ADD_A_MARK_UP_DOWN: 'Add a mark up/down',
		APPLY: 'Apply',
		REMOVE: 'Remove',
		THIS_PRODUCT_USES_THE_GLOBAL_MARKUP: 'This product uses the global markup',
		ENABLE_A_PRODUCT_SPECIFIC_MARKUP: 'Enable a product-specific markup',
		MARK_UP_DOWN_PRICES: 'Mark up/down prices',
		EDIT: 'Edit',
		SHOW_IN_SMART_SIDEBAR: 'Show in smart sidebar',
		APPLY_TO_ALL_ON_PAGE: 'Apply to all on page',
		APPLY_TO_PAGES_SAME_LAYOUT: 'Apply to pages using the same layout',
		UPDATE_ALL_SIDEBARS: 'Update all sidebars',
		NO_DATA_CAN_BE_EDITED: 'No data can be edited',
		DATA_CONTROLS: {
			PRODUCT_ID: 'Product ID',
			PRODUCT_IMAGE: 'Product photos',
			PRODUCT_NAME: 'Product name',
			DESCRIPTION: 'Description',
			MATERIALS: 'Materials',
			COLORS: 'Colors',
			SWATCHES: 'Swatches',
			SIZES: 'Sizes',
			PRICING: 'Pricing',
			MOQ: 'MOQ',
			WEBSITE: 'Call to action',
			PRODUCT_IMAGE_TWO: 'Product Image Two',
			PRICE_CODE: 'Price code',
		},
	},

	common: {
		// Actions
		SAVE: 'Save',
		CANCEL: 'Cancel',
		DELETE: 'Delete',
		EDIT: 'Edit',
		ADD: 'Add',
		CLOSE: 'Close',
		CONFIRM: 'Confirm',
		BACK: 'Back',
		CONTINUE: 'Continue',
		SKIP: 'Skip',

		// States
		LOADING: 'Loading...',
		WELCOME: 'Welcome to Smart Layouts',
		ERROR: 'Error',
		SUCCESS: 'Success',
		WARNING: 'Warning',

		// Validation
		VALUE_MUST_BE_MAX_64_CHARS: 'Value must be at the max 64 characters',
		FIELD_REQUIRED_VALID_INPUT:
			'This field is required please enter a valid input',

		// Error Messages
		UNABLE_TO_LOAD_APP_DETAILS:
			'Unable to load the application details. Please try again or contact support',
		PLEASE_TRY_AGAIN_OR_CONTACT_SUPPORT: 'Please try again or contact support.',
		TRY_REFRESHING_OR_CONTACT_SUPPORT: 'Try refreshing or contact support.',
		SOMETHING_WENT_WRONG: 'Something went wrong',
		FORBIDDEN_PROJECT_NOT_ASSOCIATED:
			'Forbidden project not associated with the company',
		PROJECT_LOCKED_RETRY_LATER:
			'Currently the project is locked, please retry later.',
		PENDING_UPDATE_DETECTED:
			'Pending update detected, applying now. Please wait...',
	},

	errors: {
		// Auth Errors
		UNABLE_TO_REFRESH_TOKEN: 'Unable to refresh token',
		UNABLE_TO_VALIDATE_EMAIL: 'Unable to validate email',
		UNABLE_TO_LOGIN: 'Unable to login (check your credentials)',
		UNABLE_TO_LOGOUT: 'Unable to logout',
		UNABLE_TO_REQUEST_PASSWORD_RESET: 'Unable to request a password reset',
		UNABLE_TO_RESET_PASSWORD: 'Unable to reset password',
		UNABLE_TO_VERIFY_RESET_PASSWORD: 'Unable to verify reset password request',
		UNABLE_TO_SIGN_UP: 'Unable to sign up',
		UNABLE_TO_RESEND_SIGNUP_VERIFICATION: 'Unable to resend signup verification',
		UNABLE_TO_VERIFY_SIGNUP: 'Unable to verify signup',

		// Product Errors
		UNABLE_TO_LOAD_PRODUCTS: 'Unable to load the products',
		UNABLE_TO_LOAD_PRODUCT_DETAILS: 'Unable to load the product details',
		UNABLE_TO_LOAD_PRODUCT_ATTRIBUTES: 'Unable to load the product attributes',

		// Asset Errors
		UNABLE_TO_LOAD_ASSET: 'Unable to load the asset',
		UNABLE_TO_PREPARE_UPLOAD: 'Unable to prepare the upload',
		UNABLE_TO_LOAD_ASSETS: 'Unable to load the assets',
		UNABLE_TO_UPLOAD_ASSET: 'Unable to upload the asset',
		UNABLE_TO_LOAD_UNWRAPPED_S3_URL: 'Unable to load the unwrapped S3 URL',
		UNABLE_TO_LOAD_IMPRINT_AREAS: 'Unable to load the imprint areas',
		UNABLE_TO_LOAD_ASSET_SETTINGS: 'Unable to load the asset settings',
		UNABLE_TO_LOAD_ASSET_VARIATIONS: 'Unable to load the asset variations',
		UNABLE_TO_CREATE_ASSET_VARIATION: 'Unable to create the asset variation',
		UNABLE_TO_LOAD_ASSET_VARIATION: 'Unable to load the asset variation',

		// Virtual Sample Errors
		UNABLE_TO_LOAD_VIRTUAL_SAMPLE: 'Unable to load the virtual sample',
		UNABLE_TO_LOAD_VIRTUAL_SAMPLES: 'Unable to load the virtual samples',
		UNABLE_TO_CREATE_VIRTUAL_SAMPLE: 'Unable to create the virtual sample',
		UNABLE_TO_EDIT_VIRTUAL_SAMPLE: 'Unable to edit the virtual sample',
		UNABLE_TO_CREATE_VIRTUAL_SAMPLES: 'Unable to create the virtual samples',
	},

	productFilters: {
		TYPE_AND_SEARCH: 'Type & search',
		NEW_ARRIVALS: 'New Arrivals',
		PRICE: 'Price',
		CATEGORY: 'Category',
		BRAND: 'Brand',
		TYPE: 'Type',
		DEPARTMENT: 'Department',
		LICENSE: 'License',
		TREND: 'Trend',
		SEASON: 'Season',
		ADD_FILTER: 'Add filter',
	},

	templateSettings: {
		WATCH_TUTORIAL: 'Watch tutorial',
		LET_OTHERS_DUPLICATE_AND_EDIT_YOUR_DESIGN:
			'Turn your design into an editable template.',
		YOU_CAN_SHARE_YOUR_DESIGN:
			'Sharing as a template lets others duplicate and customize your design as their own.',
		DO_YOU_WANT_TO_MAKE_THIS_DESIGN_A_TEMPLATE:
			'Do you want to make this design a shareable template?',
		A_TEMPLATE_ALLOWS_OTHERS_TO_DUPLICATE_YOUR_DESIGN:
			'A template allows others to duplicate your design and use it for themselves.',
	},

	shareTemplateModal: {
		SEND_LINK_DESCRIPTION_TEMPLATE:
			'Sharing as a template lets others duplicate and customize your design as their own.',
		ADD_TEMPLATE_PORTAL_TITLE: 'Add template to the template library',
		ADD_TEMPLATE_PORTAL_DESCRIPTION:
			'When enabled, this template will be made available in your ZOOMstudio template library for distributors and your team to make their own.',
		SET_TEMPLATES_PERMISSIONS_TITLE: 'Set your templates permissions',
		SET_TEMPLATES_PERMISSIONS_DESCRIPTION:
			'Choose who can see and use this template.',
		ONLY_YOU: 'Only you',
		YOUR_TEAM: 'Your team',
		PUBLIC: 'Public',
	},

	virtualsTab: {
		WATCH_VIDEO: 'Watch video',
		TITLE: 'Create Virtual Samples',
		SUBTITLE:
			'Upload a logo to automatically apply it to the products in this design.',
		SELECT_YOUR_ARTWORK: 'Select logo',
		USE_VIRTUALS_BY_DEFAULT: 'Apply logos to products',
	},

	virtualsProcessor: {
		FETCHING_PAGES: 'Gathering pages',
		FETCHING_IMPRINT_AREAS: 'Searching for imprint areas',
		QUEUE: 'Queueing for processing',
		PROCESSING_VIRTUAL: 'Processing virtual',
		IN_PROGRESS: (count: number, total: number) =>
			`Processing ${count} of ${total} virtuals`,
	},

	assetPicker: {
		FILE_TOO_LARGE: 'Sorry, your file is too large',
		FILE_TOO_SMALL: 'Sorry, your file is too small',
		TOO_MANY_FILES: 'Sorry, you tried to upload too many files',
		INVALID_FILE_TYPE: 'Sorry, we only support JPG/JPEG/PNG',
		SOMETHING_WENT_WRONG: 'Something went wrong when selecting the asset',
		UPDATE_ERROR: 'An error occurred while updating. Please refresh the page.',
	},

	virtualSampleEditor: {
		REPOSITION_LOGO: 'Reposition logo',
		SAVE_CHANGES: 'Save changes',
		DISCLAIMER_TEXT:
			"Warning: Virtual samples are for illustrative purposes only and do not guarantee the final printed product's appearance. The product's imprint areas may have restrictions on logo placement, color and size.",
	},

	logoEditor: {
		EDIT_LOGO: 'Edit logo',
	},

	productImageField: {
		IMAGES_AND_VIRTUAL_SAMPLES: 'Images and Virtual Samples',
		IMAGE_SINGULAR: 'Image',
		IMAGES_PLURAL: 'Images',
	},

	noPermission: {
		TITLE: "You don't have permissions to edit this design",
		DESCRIPTION: 'You can create a new design or open one from your library',
		CTA: 'Create new design',
	},
}

export type LocaleMessages = typeof en
